import type {
  SignupActivityKey,
  SignupServiceMode,
  SignupSpeciesKey,
} from './signupActivityCatalog';

export const SIGNUP_ACTIVITY_STORAGE_KEY = 'signup_activity_v1';

export type SignupActivitySessionV1 = {
  /** Modes sélectionnés (multi). */
  modes: SignupServiceMode[];
  /** Premier mode — rétrocompat lecture / analytics. */
  mode: SignupServiceMode | null;
  activities: SignupActivityKey[];
  species: SignupSpeciesKey[];
  profession_ids: number[];
};

function normalizeModes(
  data: Partial<SignupActivitySessionV1> & { mode?: SignupServiceMode | null }
): SignupServiceMode[] {
  if (Array.isArray(data.modes) && data.modes.length > 0) {
    return data.modes as SignupServiceMode[];
  }
  if (data.mode) return [data.mode as SignupServiceMode];
  return [];
}

export function parseSignupActivitySession(
  raw: string | null | undefined
): SignupActivitySessionV1 | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<SignupActivitySessionV1>;
    if (!Array.isArray(data.activities) || !Array.isArray(data.species)) return null;
    const modes = normalizeModes(data);
    return {
      modes,
      mode: modes[0] ?? null,
      activities: data.activities as SignupActivityKey[],
      species: data.species as SignupSpeciesKey[],
      profession_ids: Array.isArray(data.profession_ids)
        ? data.profession_ids.filter((id): id is number => typeof id === 'number')
        : [],
    };
  } catch {
    return null;
  }
}

export function readSignupActivitySession(
  storage: Pick<Storage, 'getItem'> | null | undefined = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : null
): SignupActivitySessionV1 | null {
  if (!storage) return null;
  try {
    return parseSignupActivitySession(storage.getItem(SIGNUP_ACTIVITY_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Hint i18n / wizard : le *goût* du 1er service catalogue.
 * Mixed pension + séances : reste `sessions|sitting|grooming` (le wizard service).
 * Pension-only → `stays`.
 */
export type OnboardingSignupHint =
  | 'sessions'
  | 'sitting'
  | 'grooming'
  | 'stays'
  | 'default';

/** Les 3 parcours produit. */
export type OnboardingLane = 'services' | 'stays' | 'mixed' | 'default';

const SERVICE_ACTIVITY_KEYS: readonly SignupActivityKey[] = [
  'education',
  'behavior',
  'walking',
  'leisure_sport',
  'grooming',
  'pet_sitting',
];

const SERVICE_MODES: readonly SignupServiceMode[] = [
  'sessions',
  'sitting',
  'grooming',
];

export function getOnboardingHintFromSignupActivity(
  session: SignupActivitySessionV1 | null
): OnboardingSignupHint {
  if (!session) return 'default';
  const modes = session.modes?.length
    ? session.modes
    : session.mode
      ? [session.mode]
      : [];

  if (modes.includes('grooming') || session.activities.includes('grooming')) {
    return 'grooming';
  }
  if (modes.includes('sitting') || session.activities.includes('pet_sitting')) {
    return 'sitting';
  }
  if (
    modes.includes('sessions') ||
    session.activities.some((a) =>
      ['education', 'behavior', 'walking', 'leisure_sport'].includes(a)
    )
  ) {
    return 'sessions';
  }
  if (modes.includes('stays') || session.activities.includes('boarding')) {
    return 'stays';
  }
  return 'default';
}

export function getOnboardingLaneFromSignupActivity(
  session: SignupActivitySessionV1 | null
): OnboardingLane {
  if (!session) return 'default';
  const modes = session.modes?.length
    ? session.modes
    : session.mode
      ? [session.mode]
      : [];
  const hasBoarding =
    modes.includes('stays') || session.activities.includes('boarding');
  const hasServices =
    modes.some((m) => SERVICE_MODES.includes(m)) ||
    session.activities.some((a) => SERVICE_ACTIVITY_KEYS.includes(a));
  if (hasBoarding && hasServices) return 'mixed';
  if (hasBoarding) return 'stays';
  if (hasServices) return 'services';
  return 'default';
}

export function isStaysOnlyLane(lane: OnboardingLane): boolean {
  return lane === 'stays';
}

export function isPensionTrack(lane: OnboardingLane): boolean {
  return lane === 'stays' || lane === 'mixed';
}

export function isServicesTrack(lane: OnboardingLane): boolean {
  return lane === 'services' || lane === 'mixed' || lane === 'default';
}
